import { Button, Heading, Text } from "@instacover/ui"
import { createFileRoute, useNavigate } from "@tanstack/react-router"
import { Trans, useTranslation } from "react-i18next"

import { ClientRedirect } from "@/components/ClientRedirect"
import { ExtraIcons } from "@/components/icons/InstructionImageType"
import { ImageLayout } from "@/components/layouts/ImageLayout"
import { LINKS } from "@/routing/links"
import { BrowserSupport, resolveCurrentBrowserSupport } from "@/utils/browserSupport"
import { markBrowserWarningDismissed } from "@/utils/browserWarningTracker"
import { copyToClipboard } from "@/utils/clipboard"

export const Route = createFileRoute("/$sessionId/_session/unsupported-device")({
  component: UnsupportedDeviceRoute,
})

function UnsupportedDeviceRoute() {
  const { t } = useTranslation()
  const { sessionId } = Route.useParams()
  const navigate = useNavigate()

  const browserSupport = resolveCurrentBrowserSupport()

  // Someone opened the URL by hand on a device that is fine, send them back to the inspection
  if (browserSupport === BrowserSupport.SUPPORTED) {
    return <ClientRedirect to={LINKS.root(sessionId)} />
  }

  const isDesktop = browserSupport === BrowserSupport.DESKTOP

  function handleCopyLink() {
    copyToClipboard(`${window.location.origin}${LINKS.root(sessionId)}`)
  }

  function handleContinue() {
    markBrowserWarningDismissed(sessionId)
    navigate({ to: LINKS.root(sessionId), replace: true })
  }

  return (
    <ImageLayout
      baseLayoutProps={{ hideCounter: true }}
      iconName={ExtraIcons.IncompatiblePhone}
      content={
        <>
          <Heading as="h2" variant="h1">
            {isDesktop ? t("unsupported.desktopTitle") : t("unsupported.browserTitle")}
          </Heading>

          <div className="h-6 w-6 shrink-0" />

          <Text>
            <Trans
              i18nKey={isDesktop ? "unsupported.desktopText" : "unsupported.browserText"}
              components={{ bold: <strong className="font-bold" /> }}
            />
          </Text>
        </>
      }
      footer={
        isDesktop ? undefined : (
          <div className="mb-8 flex w-full flex-col gap-2">
            <Button variant="primary" onClick={handleCopyLink} data-testid="unsupported-copy-link-btn">
              {t("unsupported.copyLink")}
            </Button>

            <Button variant="secondary" onClick={handleContinue} data-testid="unsupported-continue-btn">
              {t("unsupported.continue")}
            </Button>
          </div>
        )
      }
    />
  )
}
